import { canCancel, PurchaseItemStatus } from "common";
import {
    FulfillmentUnitStatus,
    LineItem,
    LineItemType,
    Merchandise,
    MerchandiseFulfillmentPolicy,
    MerchandiseFulfillmentStatus,
    MerchandisePurchaseItem,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
} from "types";
import { queryClient } from "@/api/queryClient";
import { isInventoryTracked, MERCHANDISE_CACHE_KEY } from "@/models/Merchandise";

export const getMerchandisePurchaseItems = (purchase: Purchase): MerchandisePurchaseItem[] => {
    return purchase.items.filter((item): item is MerchandisePurchaseItem => item.object === PurchaseItemObject.WARE);
};

const getCachedMerchandise = (productId: ID): Merchandise | undefined => {
    return (
        queryClient.getQueryData<Merchandise>([MERCHANDISE_CACHE_KEY, productId]) ??
        queryClient.getQueryData<Merchandise>(["products", productId])
    );
};

export const getInventoryEnabledMerchandisePurchaseItems = (purchase: Purchase): MerchandisePurchaseItem[] => {
    return getMerchandisePurchaseItems(purchase).filter((item) => {
        const merchandise = getCachedMerchandise(item.product.id);
        return merchandise ? isInventoryTracked(merchandise) : false;
    });
};

export const getNonCanceledMerchandisePurchaseItems = (purchase: Purchase): MerchandisePurchaseItem[] => {
    return getMerchandisePurchaseItems(purchase).filter((item) => item.status !== PurchaseItemStatus.CANCELED);
};

export const getCancelableMerchandisePurchaseItems = (purchase: Purchase): MerchandisePurchaseItem[] => {
    return getMerchandisePurchaseItems(purchase).filter((item) => canCancel(item));
};

export const isMerchandisePurchaseItemFulfilled = (item: MerchandisePurchaseItem): boolean => {
    return item.fulfillmentStatus === MerchandiseFulfillmentStatus.FULFILLED;
};

export const hasUnfulfilledMerchandise = (merchandiseItems: MerchandisePurchaseItem[]): boolean => {
    return merchandiseItems.some((item) => !isMerchandisePurchaseItemFulfilled(item));
};

export const hasIncompleteAutoFulfillment = (purchase: Purchase): boolean => {
    return getMerchandisePurchaseItems(purchase).some((item) => {
        if (item.fulfillmentPolicy !== MerchandiseFulfillmentPolicy.IMMEDIATE) {
            return false;
        }

        if (item.fulfillmentStatus && item.fulfillmentStatus !== MerchandiseFulfillmentStatus.FULFILLED) {
            return true;
        }

        return (item.fulfillmentUnits ?? []).some((unit) => unit.status !== FulfillmentUnitStatus.FULFILLED);
    });
};

export const getFulfilledQuantity = (item: MerchandisePurchaseItem, templateId: ID): number => {
    return (
        item.fulfillmentUnits?.filter(
            (unit) => unit.template?.id === templateId && unit.status === FulfillmentUnitStatus.FULFILLED,
        ).length ?? 0
    );
};

export const getMerchandiseWareLineItems = (item: MerchandisePurchaseItem | PurchaseItem): LineItem[] => {
    return item.lineItems.filter((lineItem) => lineItem.type === LineItemType.WARE);
};

export const getPurchasedWareLineItems = (item: MerchandisePurchaseItem | PurchaseItem): LineItem[] => {
    return getMerchandiseWareLineItems(item).filter((lineItem) => (lineItem.quantity ?? 0) > 0);
};

const getCountableFulfillmentUnits = (item: MerchandisePurchaseItem) => {
    return (item.fulfillmentUnits ?? []).filter((unit) => {
        return unit.status !== FulfillmentUnitStatus.RETURNED && unit.status !== FulfillmentUnitStatus.DAMAGED;
    });
};

export const getMerchandiseCancellationCounts = (
    item: MerchandisePurchaseItem,
): { willCancel: number; willReturn: number } => {
    const hasFulfillmentUnits = (item.fulfillmentUnits?.length ?? 0) > 0;
    const countableUnits = getCountableFulfillmentUnits(item);
    const totalUnits = hasFulfillmentUnits ? countableUnits.length : item.quantity ?? 0;
    const fulfilledUnits = hasFulfillmentUnits
        ? countableUnits.filter((unit) => unit.status === FulfillmentUnitStatus.FULFILLED).length
        : 0;

    return {
        willCancel: Math.max(totalUnits - fulfilledUnits, 0),
        willReturn: Math.max(fulfilledUnits, 0),
    };
};

/**
 * Calculate total and fulfilled item counts for a merchandise purchase
 */
export const getMerchandiseFulfillmentCounts = (purchase: Purchase): { fulfilled: number; total: number } => {
    const merchandiseItems = getMerchandisePurchaseItems(purchase);

    let total = 0;
    let fulfilled = 0;

    for (const item of merchandiseItems) {
        const countableUnits = getCountableFulfillmentUnits(item);
        const itemTotal = countableUnits.length;
        const itemFulfilled = countableUnits.filter((unit) => unit.status === FulfillmentUnitStatus.FULFILLED).length;

        total += itemTotal;
        fulfilled += itemFulfilled;
    }

    return { fulfilled, total };
};
